import React, { useEffect, useState, useCallback } from "react";
import { Box, Grid, Typography, CircularProgress, Alert, alpha, ToggleButtonGroup, ToggleButton } from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import DonutLargeIcon from "@mui/icons-material/DonutLarge";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import SavingsIcon from "@mui/icons-material/Savings";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import PercentIcon from "@mui/icons-material/Percent";
import useFetchWithMsal from "../../hooks/useFetchWithMsal";
import { protectedResources } from "../../authConfig";
import NotFoundPage from "../ErrorPage";
import DashboardCard from "../../components/StatisticsDashboard/DashboardCard";
import MonthRangeFilter from "../../components/StatisticsDashboard/MonthRangeFilter";
import TrendAreaChart from "../../components/StatisticsDashboard/TrendAreaChart";
import StatusBreakdownChart from "../../components/StatisticsDashboard/StatusBreakdownChart";
import CategoryPieChart from "../../components/StatisticsDashboard/CategoryPieChart";
import CategoryProgressList from "../../components/StatisticsDashboard/CategoryProgressList";
import SavingsChart from "../../components/StatisticsDashboard/SavingsChart";
import { CATEGORICAL_COLORS, getDefaultMonthRange, monthInputToRange } from "../../components/StatisticsDashboard/chartUtils";

const BASE_URL = (process.env.REACT_APP_BACKEND_BASE_URL || "").trim();
const PAGE_COLOR = "#db2777";

// Registry of companies shown in the breakdown card — adding a company later
// is just one more entry here (key must match the field the backend sends).
const ORDER_COMPANY_GROUPS = [
  { key: "eldan", label: "אלדן", color: CATEGORICAL_COLORS[0], icon: FiberManualRecordIcon },
  { key: "promedico", label: "פרומדיקו", color: CATEGORICAL_COLORS[3], icon: FiberManualRecordIcon },
];

// Hebrew display labels for raw health_fund values from the DB — falls back
// to the raw value for any fund not yet mapped here, so a new fund still
// shows up (in its original form) without needing a code change.
const HEALTH_FUND_LABELS = {
  leumit: "לאומית",
  meuhedet: "מאוחדת",
  maccabi: "מכבי",
  maccabi_sharon: "מכבי שרון",
  clalit: "כללית",
};

// The 4 view options for the top toolbar 
const DASHBOARD_SECTIONS = [
  { key: "all", label: "כללי" },
  { key: "orders", label: "הזמנות" },
  { key: "commitments", label: "התחייבויות" },
  { key: "returns", label: "החזרות" },
];

// Entry to /OrderIntakeDashboard only requires the "ManagersDashboardPage" role
// permission (checked by ProtectedRoute). Whether this specific sub-dashboard
// is enabled is a separate, narrower grant living in managerDashboardPermissionModel
// — so it's checked here, locally, against /api/managerDashboard/myAccess.
const ORDER_INTAKE_DASHBOARD_KEY = "orderIntake";

const { fromMonthInput: DEFAULT_FROM, toMonthInput: DEFAULT_TO } = getDefaultMonthRange();

const OrderIntakeDashboardPage = () => {
  // "checking" | "granted" | "denied"
  const [dashboardAccess, setDashboardAccess] = useState("checking");
  const [activeSection, setActiveSection] = useState("all");
  const [fromMonth, setFromMonth] = useState(DEFAULT_FROM);
  const [toMonth, setToMonth] = useState(DEFAULT_TO);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [healthFundStats, setHealthFundStats] = useState([]);
  const [returnsStats, setReturnsStats] = useState([]);
  const [combinedMonthlyStats, setCombinedMonthlyStats] = useState([]);
  const [commitmentsMonthlyStats, setCommitmentsMonthlyStats] = useState([]);
  const [returnsMonthlyStats, setReturnsMonthlyStats] = useState([]);
  const [minutesSavedPerOrder, setMinutesSavedPerOrder] = useState(10);
  const [minutesInput, setMinutesInput] = useState("10");
  const [savingMinutes, setSavingMinutes] = useState(false);
  const [minutesSavedPerCommitment, setMinutesSavedPerCommitment] = useState(10);
  const [minutesInputCommitment, setMinutesInputCommitment] = useState("10");
  const [savingMinutesCommitment, setSavingMinutesCommitment] = useState(false);
  const [minutesSavedPerReturn, setMinutesSavedPerReturn] = useState(10);
  const [minutesInputReturn, setMinutesInputReturn] = useState("10");
  const [savingMinutesReturn, setSavingMinutesReturn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { execute: fetchRead } = useFetchWithMsal({ scopes: protectedResources.apiScopes.scopes.read });
  const { execute: fetchWrite } = useFetchWithMsal({ scopes: protectedResources.apiScopes.scopes.write });

  useEffect(() => {
    let cancelled = false;

    // A definitive 403 means the backend evaluated access and said no.
    // Anything else (network hiccup, cold-start 5xx, token refresh blip) is a
    // failed check, not a denial — retry once before giving up.
    const checkAccess = async (isRetry = false) => {
      try {
        const result = await fetchRead("GET", `${BASE_URL}/api/managerDashboard/myAccess`);
        if (cancelled) return;
        const allowed = !!result?.isAdmin || !!result?.allowedDashboards?.includes(ORDER_INTAKE_DASHBOARD_KEY);
        setDashboardAccess(allowed ? "granted" : "denied");
      } catch (err) {
        if (cancelled) return;
        if (err?.status === 403 || isRetry) {
          setDashboardAccess("denied");
          return;
        }
        setTimeout(() => checkAccess(true), 500);
      }
    };

    checkAccess();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await fetchRead("GET", `${BASE_URL}/api/orderIntakeDashboard/settings`);
        const value = res.settings?.minutesSavedPerOrder ?? 10;
        setMinutesSavedPerOrder(value);
        setMinutesInput(String(value));
        const commitmentValue = res.settings?.minutesSavedPerCommitment ?? 10;
        setMinutesSavedPerCommitment(commitmentValue);
        setMinutesInputCommitment(String(commitmentValue));
        const returnValue = res.settings?.minutesSavedPerReturn ?? 10;
        setMinutesSavedPerReturn(returnValue);
        setMinutesInputReturn(String(returnValue));
      } catch (err) {
        console.error("Error loading order-intake-dashboard stats settings:", err);
      }
    };
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStats = useCallback(async () => {
    if (!fromMonth || !toMonth) return;
    setLoading(true);
    setError(null);
    try {
      const { startDate, endDate } = monthInputToRange(fromMonth, toMonth);
      const params = new URLSearchParams({ startDate, endDate }).toString();
      const [monthlyRes, healthFundRes, returnsRes, combinedRes, commitmentsRes, returnsMonthlyRes] = await Promise.all([
        fetchRead("GET", `${BASE_URL}/api/orderIntakeDashboard/monthly-stats?${params}`),
        fetchRead("GET", `${BASE_URL}/api/orderIntakeDashboard/health-fund-stats?${params}`),
        fetchRead("GET", `${BASE_URL}/api/orderIntakeDashboard/returns-stats?${params}`),
        fetchRead("GET", `${BASE_URL}/api/orderIntakeDashboard/combined-monthly-stats?${params}`),
        fetchRead("GET", `${BASE_URL}/api/orderIntakeDashboard/commitments-monthly-stats?${params}`),
        fetchRead("GET", `${BASE_URL}/api/orderIntakeDashboard/returns-monthly-stats?${params}`),
      ]);
      setMonthlyStats(monthlyRes.monthlyStats || []);
      setHealthFundStats(
        (healthFundRes.healthFundStats || []).map((d) => ({ ...d, name: HEALTH_FUND_LABELS[d.name] || d.name }))
      );
      setReturnsStats(returnsRes.returnsStats || []);
      setCombinedMonthlyStats(combinedRes.combinedMonthlyStats || []);
      setCommitmentsMonthlyStats(commitmentsRes.commitmentsMonthlyStats || []);
      setReturnsMonthlyStats(returnsMonthlyRes.returnsMonthlyStats || []);
    } catch (err) {
      setError(err?.msg || "שגיאה בטעינת סטטיסטיקות ההזמנות");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromMonth, toMonth]);

  useEffect(() => {
    if (dashboardAccess === "granted") loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardAccess, fromMonth, toMonth]);

  const handleSaveMinutes = async () => {
    const value = Number(minutesInput);
    if (Number.isNaN(value) || value < 0) return;
    setSavingMinutes(true);
    try {
      const res = await fetchWrite("PUT", `${BASE_URL}/api/orderIntakeDashboard/settings`, { minutesSavedPerOrder: value });
      setMinutesSavedPerOrder(res.settings?.minutesSavedPerOrder ?? value);
    } catch (err) {
      console.error("Error saving order-intake-dashboard stats settings:", err);
    } finally {
      setSavingMinutes(false);
    }
  };

  const handleSaveMinutesCommitment = async () => {
    const value = Number(minutesInputCommitment);
    if (Number.isNaN(value) || value < 0) return;
    setSavingMinutesCommitment(true);
    try {
      const res = await fetchWrite("PUT", `${BASE_URL}/api/orderIntakeDashboard/settings`, { minutesSavedPerCommitment: value });
      setMinutesSavedPerCommitment(res.settings?.minutesSavedPerCommitment ?? value);
    } catch (err) {
      console.error("Error saving order-intake-dashboard commitment stats settings:", err);
    } finally {
      setSavingMinutesCommitment(false);
    }
  };

  const handleSaveMinutesReturn = async () => {
    const value = Number(minutesInputReturn);
    if (Number.isNaN(value) || value < 0) return;
    setSavingMinutesReturn(true);
    try {
      const res = await fetchWrite("PUT", `${BASE_URL}/api/orderIntakeDashboard/settings`, { minutesSavedPerReturn: value });
      setMinutesSavedPerReturn(res.settings?.minutesSavedPerReturn ?? value);
    } catch (err) {
      console.error("Error saving order-intake-dashboard return stats settings:", err);
    } finally {
      setSavingMinutesReturn(false);
    }
  };

  if (dashboardAccess === "checking") {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: PAGE_COLOR }} />
      </Box>
    );
  }

  if (dashboardAccess === "denied") {
    return <NotFoundPage />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, display: "flex", flexDirection: "column", gap: 3 }}>
      <Box display="flex" alignItems="center" gap={2}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            bgcolor: alpha(PAGE_COLOR, 0.12),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <BarChartIcon sx={{ color: PAGE_COLOR, fontSize: 26 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700}>קליטת הזמנות</Typography>
          <Typography variant="body2" color="text.secondary">
            תמונת מצב לפי טווח התאריכים שנבחר — משתנה בכל הדשבורדים למטה
          </Typography>
        </Box>
      </Box>

      <ToggleButtonGroup
        value={activeSection}
        exclusive
        onChange={(_, value) => value && setActiveSection(value)}
        size="small"
        sx={{ bgcolor: "#fff", borderRadius: 3, p: 0.5, border: `1px solid ${alpha(PAGE_COLOR, 0.4)}`, alignSelf: "flex-start" }}
      >
        {DASHBOARD_SECTIONS.map((section) => (
          <ToggleButton
            key={section.key}
            value={section.key}
            sx={{
              borderRadius: 2.5,
              px: 2,
              textTransform: "none",
              fontSize: 14,
              color: "text.primary",
              "&.Mui-selected": {
                fontWeight: 700,
                color: "text.primary",
                bgcolor: alpha(PAGE_COLOR, 0.12),
                "&:hover": { bgcolor: alpha(PAGE_COLOR, 0.18) },
              },
            }}
          >
            {section.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <MonthRangeFilter
        fromMonth={fromMonth}
        toMonth={toMonth}
        onFromMonthChange={setFromMonth}
        onToMonthChange={setToMonth}
        color={PAGE_COLOR}
      />

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box display="flex" flexDirection="column" alignItems="center" gap={1.5} p={6}>
          <CircularProgress sx={{ color: PAGE_COLOR }} />
          <Typography variant="body2" color="text.secondary">טוען נתונים...</Typography>
        </Box>
      ) : (
        <Grid container spacing={3} alignItems="stretch">
          {activeSection === "orders" && (
            <Grid item xs={12} md={6}>
              <DashboardCard
                title="מגמת הזמנות חודשית"
                subtitle="סה״כ הזמנות בתי חולים"
                icon={TrendingUpIcon}
                color={PAGE_COLOR}
              >
                {(expanded) => (
                  <TrendAreaChart
                    monthlyData={monthlyStats}
                    expanded={expanded}
                    color={PAGE_COLOR}
                    totalLabel="סה״כ הזמנות בתי חולים"
                    unitLabel="הזמנות"
                    gradientId="orderIntakeTrendGradient"
                  />
                )}
              </DashboardCard>
            </Grid>
          )}

          {activeSection === "all" && (
            <Grid item xs={12} md={6}>
              <DashboardCard
                title="מגמת הזמנות חודשית כללית"
                subtitle="הזמנות + התחייבויות + החזרות"
                icon={TrendingUpIcon}
                color={PAGE_COLOR}
              >
                {(expanded) => (
                  <TrendAreaChart
                    monthlyData={combinedMonthlyStats}
                    expanded={expanded}
                    color={PAGE_COLOR}
                    totalLabel="סה״כ בטווח שנבחר"
                    unitLabel="פעולות"
                    gradientId="orderIntakeCombinedTrendGradient"
                  />
                )}
              </DashboardCard>
            </Grid>
          )}

          {activeSection === "orders" && (
            <Grid item xs={12} md={6}>
              <DashboardCard
                title="חיסכון בזמן עבודה"
                subtitle="לפי מספר ההזמנות בטווח שנבחר"
                icon={SavingsIcon}
                color={CATEGORICAL_COLORS[2]}
              >
                {(expanded) => (
                  <SavingsChart
                    monthlyData={monthlyStats}
                    color={CATEGORICAL_COLORS[2]}
                    minutesSavedPerUnit={minutesSavedPerOrder}
                    minutesInput={minutesInput}
                    onMinutesInputChange={setMinutesInput}
                    onSaveMinutes={handleSaveMinutes}
                    saving={savingMinutes}
                    expanded={expanded}
                    minutesFieldLabel="דקות שנחסכות להזמנה"
                  />
                )}
              </DashboardCard>
            </Grid>
          )}

          {activeSection === "all" && (
            <Grid item xs={12} md={6}>
              <DashboardCard
                title="חיסכון בזמן עבודה"
                subtitle="הזמנות + התחייבויות + החזרות"
                icon={SavingsIcon}
                color={CATEGORICAL_COLORS[2]}
              >
                {(expanded) => (
                  <SavingsChart
                    monthlyData={combinedMonthlyStats}
                    color={CATEGORICAL_COLORS[2]}
                    minutesSavedPerUnit={minutesSavedPerOrder}
                    minutesInput={minutesInput}
                    onMinutesInputChange={setMinutesInput}
                    onSaveMinutes={handleSaveMinutes}
                    saving={savingMinutes}
                    expanded={expanded}
                    minutesFieldLabel="דקות שנחסכות לפעולה"
                  />
                )}
              </DashboardCard>
            </Grid>
          )}

          {(activeSection === "all" || activeSection === "orders") && (
            <Grid item xs={12} md={6}>
              <DashboardCard
                title="הזמנות בתי חולים לפי חברה"
                subtitle="מגמה חודשית — אלדן / פרומדיקו"
                icon={FactCheckIcon}
                color={CATEGORICAL_COLORS[1]}
              >
                {(expanded) => (
                  <StatusBreakdownChart
                    monthlyData={monthlyStats}
                    expanded={expanded}
                    statusGroups={ORDER_COMPANY_GROUPS}
                    totalLabel="סה״כ הזמנות בתי חולים"
                  />
                )}
              </DashboardCard>
            </Grid>
          )}

          {(activeSection === "all" || activeSection === "commitments") && (
            <Grid item xs={12} md={6}>
              <DashboardCard title="התחייבות מרשמים לפי קופה" icon={DonutLargeIcon} color={CATEGORICAL_COLORS[6]}>
                {(expanded) => (
                  <CategoryPieChart data={healthFundStats} expanded={expanded} unitLabel="התחייבויות" />
                )}
              </DashboardCard>
            </Grid>
          )}

          {activeSection === "commitments" && (
            <Grid item xs={12} md={6}>
              <DashboardCard
                title="חיסכון בזמן עבודה"
                subtitle="לפי מספר ההתחייבויות בטווח שנבחר"
                icon={SavingsIcon}
                color={CATEGORICAL_COLORS[2]}
              >
                {(expanded) => (
                  <SavingsChart
                    monthlyData={commitmentsMonthlyStats}
                    color={CATEGORICAL_COLORS[2]}
                    minutesSavedPerUnit={minutesSavedPerCommitment}
                    minutesInput={minutesInputCommitment}
                    onMinutesInputChange={setMinutesInputCommitment}
                    onSaveMinutes={handleSaveMinutesCommitment}
                    saving={savingMinutesCommitment}
                    expanded={expanded}
                    minutesFieldLabel="דקות שנחסכות להתחייבות"
                  />
                )}
              </DashboardCard>
            </Grid>
          )}

          {(activeSection === "all" || activeSection === "commitments") && (
            <Grid item xs={12} md={6}>
              <DashboardCard title="אחוז הצלחת ההתחייבויות לפי קופה" icon={TaskAltIcon} color={CATEGORICAL_COLORS[2]}>
                {() => (
                  <CategoryProgressList data={healthFundStats} unitLabel="התחייבויות" secondaryMetricLabel="הצליחו" />
                )}
              </DashboardCard>
            </Grid>
          )}

          {(activeSection === "all" || activeSection === "returns") && (
            <Grid item xs={12} md={6}>
              <DashboardCard title="החזרות לפי קופה" icon={AssignmentReturnIcon} color={CATEGORICAL_COLORS[7]}>
                {(expanded) => (
                  <CategoryPieChart data={returnsStats} expanded={expanded} unitLabel="החזרות" />
                )}
              </DashboardCard>
            </Grid>
          )}

          {activeSection === "returns" && (
            <Grid item xs={12} md={6}>
              <DashboardCard
                title="חיסכון בזמן עבודה"
                subtitle="לפי מספר ההחזרות בטווח שנבחר"
                icon={SavingsIcon}
                color={CATEGORICAL_COLORS[2]}
              >
                {(expanded) => (
                  <SavingsChart
                    monthlyData={returnsMonthlyStats}
                    color={CATEGORICAL_COLORS[2]}
                    minutesSavedPerUnit={minutesSavedPerReturn}
                    minutesInput={minutesInputReturn}
                    onMinutesInputChange={setMinutesInputReturn}
                    onSaveMinutes={handleSaveMinutesReturn}
                    saving={savingMinutesReturn}
                    expanded={expanded}
                    minutesFieldLabel="דקות שנחסכות להחזרה"
                  />
                )}
              </DashboardCard>
            </Grid>
          )}

          {(activeSection === "all" || activeSection === "returns") && (
            <Grid item xs={12} md={6}>
              <DashboardCard title="מגמת הצלחת הריצה בהחזרות לפי קופה" icon={PercentIcon} color={CATEGORICAL_COLORS[0]}>
                {() => (
                  <CategoryProgressList data={returnsStats} unitLabel="החזרות" secondaryMetricLabel="הצליחו" />
                )}
              </DashboardCard>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default OrderIntakeDashboardPage;
